import type { Tone } from "@/features/utils/colors";

/**
 * What each outcome means to whoever reads it, as a `Tone`: done (green), still
 * on the line (blue), reached nobody (orange), a message left (purple), handed
 * to a person (pink), went wrong (red), nothing recorded (grey). Semantic rather
 * than hashed from the value, so "Échec" is red because it is a failure, not
 * because of its spelling — and the same in the calls list and on the
 * dashboard's donut.
 *
 * Voicemail is purple rather than yellow: yellow and orange are both the
 * warning token, which left "no answer" and "voicemail" the same colour.
 * `transferred` has a colour of its own for the same reason: it shared
 * voicemail's purple while only a badge showed it, where the glyph told them
 * apart, but the donut has no glyph and drew them as two identical slices.
 */
export const RESULT_TONES: Record<string, Tone> = {
  success: "green",
  failure: "red",
  no_answer: "orange",
  voicemail: "purple",
  transferred: "pink",
  pending: "blue",
  unknown: "gray",
};

export const resultTone = (result: string): Tone =>
  RESULT_TONES[result] || "gray";

/**
 * The outcome a reader is shown — `pending` while the session is still on the line,
 * `no_answer` / `voicemail` when nobody answered (there was no conversation for anything
 * else to describe), `transferred` once a person took it over (whether the agent handled
 * it alone is what a reader wants to know next: "Réussi" on a call a colleague took says
 * the opposite), and `unknown` when nothing was recorded (an old row, a wrap-up that threw).
 *
 * A `failure` is only one when a tool never once succeeded (`tool_failed`): a tool made
 * good later in the call is no failure, and a verdict no failed tool backs is the
 * summariser's impression — `unknown`. Only an explicit `false` downgrades it, so a row
 * the server did not compute the flag for keeps its verdict.
 *
 * The server buckets the dashboard's donut with the same rule
 * (`sessions/services/session/outcome.ts`), so a slice is the count of one badge here —
 * except "Pas de réponse", which counts the voicemails too (`performanceBucket`).
 */
export const effectiveResult = (session: {
  state?: string;
  result?: string | null;
  transferred?: boolean;
  tool_failed?: boolean;
}): string =>
  session.state === "open"
    ? "pending"
    : session.result === "no_answer" || session.result === "voicemail"
      ? session.result
      : session.transferred
        ? "transferred"
        : session.result === "failure" && session.tool_failed === false
          ? "unknown"
          : session.result || "unknown";
